// THREE renderer / camera / scene / controls / clock / stats — the
// initialised handles created in app/bootstrap/threeStack.ts. Owned by
// ViewerContext.three.

import type * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { ViewHelper } from 'three/examples/jsm/helpers/ViewHelper.js';
import type Stats from 'stats-gl';

/** The active scene camera. Either projection is selectable at runtime; the two
 *  share enough API (position/quaternion/project/getWorldDirection) that most
 *  call sites are projection-agnostic — branch on `instanceof` only where the
 *  zoom/framing semantics actually differ (resize, marker sizing, presets). */
export type SceneCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

export interface ThreeRefs {
    scene: THREE.Scene | null;
    camera: SceneCamera | null;
    renderer: THREE.WebGLRenderer | null;
    controls: OrbitControls | null;
    clock: THREE.Clock | null;
    stats: Stats | null;
    /** Orientation gnomon. Owned by `src/ui/cameraGnomon`; null until installed. */
    viewHelper: ViewHelper | null;
    /**
     * Per-frame entry point for the gnomon — closure capturing the dedicated
     * renderer + slerp-state. Called from the render loop after the main
     * scene is drawn. Null until cameraGnomon installs.
     */
    viewHelperTick: ((dt: number) => void) | null;
}

export function createThreeRefs(): ThreeRefs {
    return {
        scene: null,
        camera: null,
        renderer: null,
        controls: null,
        clock: null,
        stats: null,
        viewHelper: null,
        viewHelperTick: null,
    };
}
